<template>
  <div class="commission-page">
    <el-card class="box-card">
      <div class="text item">
        <!-- 查询与选择学校 -->
        <el-form :inline="true" class="demo-form-inline" size="small" ref="searchForm" :model="search">
          <el-form-item label="学校" prop="schoolId">
            <el-select
              v-model="form.schoolId"
              filterable
              remote
              placeholder="请输入学校关键词"
              :remote-method="remoteSearchSchool"
              :loading="schoolLoading"
              style="min-width: 260px"
            >
              <el-option
                v-for="item in schoolOptions"
                :key="item.schoolId"
                :label="item.schoolName"
                :value="item.schoolId"
              />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button
              type="primary"
              icon="el-icon-refresh"
              size="mini"
              @click="loadConfig"
              :disabled="!form.schoolId"
            >
              载入当前配置
            </el-button>
          </el-form-item>
        </el-form>

        <!-- 分成配置表单 -->
        <el-form
          ref="commissionForm"
          :model="form"
          :rules="rules"
          label-width="120px"
          size="small"
          class="commission-form"
        >
          <el-form-item label="兼职比例(%)" prop="runnerRate">
            <el-input-number
              v-model="form.runnerRate"
              :min="0"
              :max="100"
              :precision="2"
              :step="1"
            />
          </el-form-item>

          <el-form-item label="负责人比例(%)" prop="managerRate">
            <el-input-number
              v-model="form.managerRate"
              :min="0"
              :max="100"
              :precision="2"
              :step="1"
            />
          </el-form-item>

          <el-form-item label="平台比例(%)" prop="platformRate">
            <el-input-number
              v-model="form.platformRate"
              :min="0"
              :max="100"
              :precision="2"
              :step="1"
            />
          </el-form-item>

          <el-form-item label="是否启用" prop="enabled">
            <el-switch
              v-model="form.enabled"
              :active-value="1"
              :inactive-value="0"
            />
          </el-form-item>

          <el-form-item label="当前合计">
            <span :class="['total-rate', totalRate === 100 ? 'ok' : 'error']">
              {{ totalRate.toFixed(2) }} %
            </span>
            <span class="tip">
              （必须等于 100%，否则无法保存）
            </span>
          </el-form-item>

          <el-form-item label="备注">
            <el-input
              v-model="form.remark"
              type="textarea"
              :rows="2"
              placeholder="可填写该学校的分成说明，仅后台可见"
            />
          </el-form-item>

          <el-form-item>
            <el-button
              type="primary"
              icon="el-icon-check"
              :disabled="!form.schoolId || totalRate !== 100"
              @click="onSave"
            >
              保存配置
            </el-button>
            <el-button icon="el-icon-refresh" @click="resetForm">
              重置
            </el-button>
          </el-form-item>
        </el-form>

        <el-alert
          title="说明"
          type="info"
          show-icon
          :closable="false"
          class="info-alert"
        >
          <template #description>
            <p>1. 分成比例按学校维度生效，影响该学校下所有订单的结算。</p>
            <p>2. 接单人为负责人时：负责人会拿到“兼职 + 负责人”两部分，平台拿“平台比例”。</p>
            <p>3. 接单人为普通兼职且无负责人时：兼职拿“兼职比例”，平台拿剩余部分。</p>
            <p>4. 接单人为普通兼职且有负责人时：兼职 / 负责人 / 平台分别拿对应配置比例。</p>
          </template>
        </el-alert>
      </div>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'SchoolCommission',
  data() {
    const validateTotal = (rule, value, callback) => {
      if (this.totalRate !== 100) {
        callback(new Error('三项比例之和必须为 100%'));
      } else {
        callback();
      }
    };

    return {
      search: {},
      schoolOptions: [],
      schoolLoading: false,
      form: {
        id: null,
        schoolId: null,
        runnerRate: 70,
        managerRate: 10,
        platformRate: 20,
        enabled: 1,
        remark: ''
      },
      rules: {
        schoolId: [{ required: true, message: '请选择学校', trigger: 'change' }],
        runnerRate: [
          { required: true, message: '请输入兼职比例', trigger: 'blur' },
          { validator: validateTotal, trigger: 'change' }
        ],
        managerRate: [
          { required: true, message: '请输入负责人比例', trigger: 'blur' },
          { validator: validateTotal, trigger: 'change' }
        ],
        platformRate: [
          { required: true, message: '请输入平台比例', trigger: 'blur' },
          { validator: validateTotal, trigger: 'change' }
        ]
      }
    };
  },
  computed: {
    totalRate() {
      const r = Number(this.form.runnerRate || 0);
      const m = Number(this.form.managerRate || 0);
      const p = Number(this.form.platformRate || 0);
      return r + m + p;
    }
  },
  methods: {
    // 学校远程搜索
    async remoteSearchSchool(query) {
      if (!query) {
        this.schoolOptions = [];
        return;
      }
      this.schoolLoading = true;
      try {
        const { data: res } = await this.$http.get('/school/getSchoolBySchoolName', {
          params: { schoolName: query }
        });
        if (res.code === 200) {
          this.schoolOptions = res.data || [];
        } else {
          this.$message.error(res.msg || '获取学校列表失败');
        }
      } catch (e) {
        console.error(e);
        this.$message.error('获取学校列表失败');
      } finally {
        this.schoolLoading = false;
      }
    },

    // 根据已选学校加载配置
    async loadConfig() {
      if (!this.form.schoolId) {
        this.$message.warning('请先选择学校');
        return;
      }
      try {
        const { data: res } = await this.$http.get('/commission/getBySchoolId', {
          params: { schoolId: this.form.schoolId }
        });
        if (res.code === 200 && res.data) {
          const cfg = res.data;
          this.form.id = cfg.id || null;
          // 后端存的是 0-1，小数，这里转换为百分比
          this.form.runnerRate = Number(cfg.runnerRate || 0) * 100;
          this.form.managerRate = Number(cfg.managerRate || 0) * 100;
          this.form.platformRate = Number(cfg.platformRate || 0) * 100;
          this.form.enabled = cfg.enabled != null ? cfg.enabled : 1;
          this.form.remark = cfg.remark || '';
          this.$message.success('已载入该学校当前配置');
        } else {
          // 没有配置时使用默认 70 / 10 / 20
          this.form.id = null;
          this.form.runnerRate = 70;
          this.form.managerRate = 10;
          this.form.platformRate = 20;
          this.form.enabled = 1;
          this.form.remark = '';
          this.$message.info('该学校暂无配置，已填入默认 70/10/20');
        }
      } catch (e) {
        console.error(e);
        this.$message.error('加载分成配置失败');
      }
    },

    resetForm() {
      this.form.runnerRate = 70;
      this.form.managerRate = 10;
      this.form.platformRate = 20;
      this.form.enabled = 1;
      this.form.remark = '';
      this.form.id = null;
    },

    async onSave() {
      if (!this.form.schoolId) {
        this.$message.warning('请先选择学校');
        return;
      }
      if (this.totalRate !== 100) {
        this.$message.error('三项比例之和必须为 100%');
        return;
      }
      this.$refs.commissionForm.validate(async (valid) => {
        if (!valid) {
          return;
        }
        const payload = {
          id: this.form.id,
          schoolId: this.form.schoolId,
          // 转换为 0-1 的小数，保留 4 位
          runnerRate: (Number(this.form.runnerRate) / 100).toFixed(4),
          managerRate: (Number(this.form.managerRate) / 100).toFixed(4),
          platformRate: (Number(this.form.platformRate) / 100).toFixed(4),
          enabled: this.form.enabled,
          remark: this.form.remark
        };
        try {
          const { data: res } = await this.$http.post('/commission/saveOrUpdate', payload);
          if (res.code === 200) {
            this.$message.success('保存成功');
            // 重新拉取一次，确保表单中的 id 等数据是最新的
            this.loadConfig();
          } else {
            this.$message.error(res.msg || '保存失败');
          }
        } catch (e) {
          console.error(e);
          this.$message.error('保存失败，请稍后重试');
        }
      });
    }
  }
};
</script>

<style scoped>
.commission-page {
  width: 100%;
}

.box-card {
  width: 100%;
}

.item {
  margin-bottom: 18px;
}

.commission-form {
  margin-top: 20px;
  max-width: 520px;
}

.total-rate {
  font-weight: 600;
  margin-right: 8px;
}

.total-rate.ok {
  color: #67c23a;
}

.total-rate.error {
  color: #f56c6c;
}

.tip {
  font-size: 12px;
  color: #909399;
}

.info-alert {
  margin-top: 24px;
}

.info-alert p {
  margin: 2px 0;
  font-size: 13px;
}
</style>


